import React from "react";
import { withStyles } from "@material-ui/core";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import {
  isEmpty,
  get,
  map,
  includes,
  toLower,
  filter,
  forEach,
  size,
  concat,
} from "lodash";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TableRow from "@material-ui/core/TableRow";

import { fetchGet, fetchPost } from "../../api/fetch";
import TagColor from "../Common/TagColor";
import TableCellStyled from "../Common/TableCellStyled";
import ScrollableTable from "../Common/Table/ScrollableTable";
import ProfilePictureAndLink from "../Common/ProfilePictureAndLink";
import {
  displayDataCsv,
  displayBoolean,
  exportTableToCsv,
} from "../../utils/data";
import { getLov } from "../../utils/lov";
import colors from "../../assets/style/colors";
import DownloadIcon from "../Common/DownloadIcon";

let columns = [
  {
    name: "profiles_type",
    minWidth: 180,
    filter: {
      name: "profile_type",
      type: "select",
    },
  },
  {
    name: "profiles_name",
    minWidth: 200,
    filter: {
      type: "smartsearch",
      name: "name",
      path: ["name"],
    },
  },
  {
    name: "profiles_tag",
    minWidth: 150,
    filter: {
      name: "tag",
      type: "select",
    },
  },
  {
    name: "companies_company",
    minWidth: 150,
  },
  {
    name: "companies_site",
    minWidth: 150,
  },
  {
    name: "companies_zone",
    minWidth: 150,
  },
  {
    name: "profiles_active",
    minWidth: 80,
    filter: {
      name: "active",
      type: "select",
    },
  },
];

const styles = {
  dataCell: {
    color: colors.black54,
  },
};

const AssignedProfiles = (props) => {
  const {
    classes,
    companyId,
    siteId,
    zoneId,
    language,
  } = props;
  const [profiles, setProfiles] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);
  const [lov, setLov] = React.useState();
  const [filters, setFilters] = React.useState({
    profile_type: [],
    tag: [],
    active: "",
    name: "",
  });

  React.useEffect(() => {
    const fetchProfiles = async () => {
      setIsFetching(true);
      let body = {};
      if (zoneId) {
        try {
          let zone = await fetchGet(
            `companies/${companyId}/sites/${siteId}/zones/${zoneId}`
          );
          const getZoneIds = (zone) => {
            if (size(get(zone, ["childs"])) > 0) {
              let ids = [get(zone, ["id"])];
              forEach(get(zone, ["childs"]), (z) => {
                ids = concat(ids, getZoneIds(z));
              });
              return ids;
            }
            return [get(zone, ["id"])];
          };
          body.zones = getZoneIds(zone);
        } catch (e) {
          console.log(e);
        }
      } else if (siteId) {
        body.sites = [parseInt(siteId)];
      } else if (companyId) {
        body.companies = [parseInt(companyId)];
      }
      if (!isEmpty(body)) {
        try {
          let profiles = await fetchPost("profiles/search", body);
          setProfiles(
            map(profiles, (p) => ({
              ...p,
              name:
                get(p, ["type", "id"]) === 1
                  ? `${get(p, ["firstname"])} ${get(p, ["lastname"])}`
                  : get(p, ["name"]),
            }))
          );
        } catch (e) {
          console.log(e);
        }
      }
      setIsFetching(false);
    };
    fetchProfiles();
    fetchLov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, zoneId]);

  const fetchLov = async () => {
    try {
      setLov({
        profile_type: await getLov("profileTypes"),
        tag: await getLov("tags"),
        active: [
          {
            id: true,
            name_en: I18n.get("profiles_yes"),
            name_fr: I18n.get("profiles_yes"),
          },
          {
            id: false,
            name_en: I18n.get("profiles_no"),
            name_fr: I18n.get("profiles_no"),
          },
        ],
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleFilterChange = (value, field) => {
    if (includes(value, "selectAll")) {
      value = map(get(lov, [field]), (item) => item.id);
    } else if (includes(value, "selectNone")) {
      value = [];
    }
    setFilters(
      update(filters, {
        [field]: { $set: value },
      })
    );
  };

  const applyFilters = (item) => {
    let exclude = false;
    if (
      !isEmpty(get(filters, ["profile_type"])) &&
      !includes(get(filters, ["profile_type"]), get(item, ["type", "id"]))
    ) {
      exclude = true;
    }
    if (
      !isEmpty(get(filters, ["tag"])) &&
      !includes(get(filters, ["tag"]), get(item, ["tag", "id"]))
    ) {
      exclude = true;
    }
    if (
      get(filters, ["name"]) &&
      !includes(toLower(get(item, ["name"])), toLower(get(filters, ["name"])))
    ) {
      exclude = true;
    }
    if (
      get(filters, ["active"]) &&
      !includes(get(filters, ["active"]), get(item, ["active"]))
    ) {
      exclude = true;
    }
    return !exclude;
  };

  const dataFunctions = [
    (item) => displayDataCsv(get(item, ["type", `name_${language}`])),
    (item) => displayDataCsv(get(item, ["name"])),
    (item) => displayDataCsv(get(item, ["tag", `name_${language}`])),
    (item) => displayDataCsv(get(item, ["company_name"])),
    (item) => displayDataCsv(get(item, ["site_name"])),
    (item) => displayDataCsv(get(item, ["zone_name"])),
    (item) => displayBoolean(get(item, ["active"])),
  ];

  return (
    <Grid item style={{ overflow: "auto", maxWidth: "1300px" }}>
      <Box style={{ textAlign: "right" }}>
        <DownloadIcon
          exportCsv={() =>
            exportTableToCsv(
              "assigned-profiles",
              columns,
              profiles,
              dataFunctions,
              I18n.get
            )
          }
        />
      </Box>
      <ScrollableTable
        columns={columns}
        hasMore={false}
        isFetching={isFetching}
        stickyHeader
        lov={lov}
        filters={filters}
        handleFilterChange={handleFilterChange}
        data={profiles}
      >
        {map(filter(profiles, applyFilters), (item, index) => (
          <TableRow
            key={index}
            style={{
              backgroundColor:
                index % 2 === 0 ? colors.white : colors.lightGrey,
            }}
          >
            <TableCellStyled className={classes.dataCell}>
              {get(item, ["type", `name_${language}`])}
            </TableCellStyled>
            <TableCellStyled>
              <ProfilePictureAndLink profile={item} />
            </TableCellStyled>
            <TableCellStyled>
              <TagColor
                color={get(item, ["tag", "color", "rgb"])}
                name={get(item, ["tag", `name_${language}`])}
              />
            </TableCellStyled>
            <TableCellStyled className={classes.dataCell}>
              {get(item, ["company_name"])}
            </TableCellStyled>
            <TableCellStyled className={classes.dataCell}>
              {get(item, ["site_name"])}
            </TableCellStyled>
            <TableCellStyled className={classes.dataCell}>
              {get(item, ["zone_name"])}
            </TableCellStyled>
            <TableCellStyled className={classes.dataCell}>
              {displayBoolean(get(item, ["active"]))}
            </TableCellStyled>
          </TableRow>
        ))}
      </ScrollableTable>
    </Grid>
  );
};

export default withStyles(styles)(AssignedProfiles);
